import { customFieldRepository } from '@webapp/core/data-access/repositories/CustomFieldRepository/CustomFieldRepository'
import {
  CustomField,
  type CustomFieldConstructorData,
} from '@webapp/core/entities/internal/CustomField'
import { HttpError } from '@webapp/infrastructure/interfaces/services/IHttpClient'
// eslint-disable-next-line import/no-internal-modules
import { SystemPreferencesRepository } from '@webapp/platform/iam/data-access/repositories/SystemPreferencesRepository'
// eslint-disable-next-line import/no-internal-modules
import { SystemPreferenceKeys } from '@webapp/platform/iam/entities/enums/SystemPreferenceKeys'
// eslint-disable-next-line import/no-internal-modules
import { type POPDFHiddenCustomField } from '@webapp/platform/iam/entities/SystemPreference'

const ACTIVE_CUSTOM_FIELD_PARAMS = {
  meta__is_active: 'True',
  page_size: 9999,
}

export interface POPDFCustomFieldDisplayRow {
  field: CustomField
  displayInPDF: boolean
}

export interface POPDFCustomFieldDisplay {
  poFields: POPDFCustomFieldDisplayRow[]
  orderItemFields: POPDFCustomFieldDisplayRow[]
}

// The kvstore signals "no hidden map saved yet" either as a 404 or by
// omitting the key from the GET-all response (which the repository surfaces
// as an "Invalid system preference key" error). Anything else is a real
// failure and must propagate.
const isUnsetHiddenMapError = (error: unknown): boolean => {
  if (error instanceof HttpError) {
    return error.status === 404
  }

  return (
    error instanceof Error &&
    error.message ===
      `Invalid system preference key: ${SystemPreferenceKeys.PO_PDF_CUSTOM_FIELDS_HIDDEN}`
  )
}

const getHiddenMap = async (): Promise<POPDFHiddenCustomField[]> => {
  try {
    const preference = await SystemPreferencesRepository.getSystemPreference(
      SystemPreferenceKeys.PO_PDF_CUSTOM_FIELDS_HIDDEN
    )
    return preference.value
  } catch (error) {
    if (isUnsetHiddenMapError(error)) {
      // 404 / missing key is a valid "nothing hidden yet" state — every
      // custom field is shown on the PDF (BHV-043)
      return []
    }
    throw error
  }
}

/**
 * Join the active PO + order-item custom fields with the
 * `po-pdf-custom-fields-hidden` preference map (BHV-043): a field's display
 * toggle is on when it is absent from the map or its entry has
 * `hidden: false`.
 */
export const getPOPDFCustomFieldDisplay =
  async (): Promise<POPDFCustomFieldDisplay> => {
    const [poFields, orderItemFields, hiddenMap] = await Promise.all([
      customFieldRepository.getPurchaseOrderCustomFields(
        ACTIVE_CUSTOM_FIELD_PARAMS
      ),
      customFieldRepository.getOrderItemCustomFields(
        ACTIVE_CUSTOM_FIELD_PARAMS
      ),
      getHiddenMap(),
    ])

    const hiddenByFieldId = new Map(
      hiddenMap.map((entry) => [entry.customField, entry.hidden])
    )

    const toRow = (
      field: CustomFieldConstructorData
    ): POPDFCustomFieldDisplayRow => ({
      field: new CustomField(field),
      displayInPDF: !(hiddenByFieldId.get(field.id) ?? false),
    })

    return {
      poFields: poFields.data.map(toRow),
      orderItemFields: orderItemFields.data.map(toRow),
    }
  }
